import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Select,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconFileUpload } from "@tabler/icons-react";
import i18n from "src/i18n";
import { z } from "zod";

import { getConsultationServiceCategoryValues } from "@data/dropdownValues";

import { useCreateClinicConsultationService } from "@hooks/mutations/clinic/useCreateClinicConsultationService";
import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";

import { StyledAddConsultationServiceModalContent } from "./AddConsultationServiceModal.styled";


const singleFormSchema = () =>
  z.object({
    name: z.string().min(1, i18n.t("name-required")),
    category: z.string().optional(),
    price: z.number().min(1, i18n.t("price-required")),
    status: z.string().min(1, i18n.t("status-required")),
    partner_id: z.string().optional(),
    service_duration: z.number().min(1, i18n.t("duration-required")).optional(),
    in_30_min: z.boolean().optional(),
    sessions: z.number().int().min(1, i18n.t("sessions-required")),
    session_interval_days: z
      .number()
      .int()
      .min(1, i18n.t("session-interval-days-required")),
  });

type SingleFormInputs = z.infer<ReturnType<typeof singleFormSchema>>;

interface AddConsultationServiceModalProps {
  opened: boolean;
  close: () => void;
}

export const AddConsultationServiceModal = ({
  opened,
  close,
}: AddConsultationServiceModalProps) => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState<string | null>("single");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const {
    mutate: createConsultationService,
    isLoading: isCreateConsultationServiceLoading,
  } = useCreateClinicConsultationService();

  const { data: partnersData } = useGetPartners({ isActive: true });

  const partnersOptions = useMemo(() => {
    return (partnersData || []).map(partner => ({
      label: partner.name,
      value: partner._id,
    }));
  }, [partnersData]);

  const singleForm = useForm({
    mode: "uncontrolled",
    validate: zodResolver(singleFormSchema()),
    initialValues: {
      name: "",
      category: "",
      price: 0,
      status: "active",
      partner_id: "",
      sessions: 1,
      session_interval_days: 21,
    },
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "text/csv": [".csv"],
    },
    onDrop,
    multiple: false,
    maxFiles: 1,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleSingleFormSubmit = (values: SingleFormInputs) => {
    createConsultationService(values, {
      onSuccess: () => {
        singleForm.reset();
        close();
      },
    });
  };

  const handleBatchSubmit = () => {
    // TODO: Implement batch upload functionality
    console.log("Batch upload files:", uploadedFiles);
    close();
  };

  const handleClose = () => {
    singleForm.reset();
    setUploadedFiles([]);
    setCurrentTab("single");
    close();
  };

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("add-consultation-service")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
      size="md">
      <StyledAddConsultationServiceModalContent>
        <Tabs value={currentTab} onChange={setCurrentTab}>
          <Tabs.List grow>
            <Tabs.Tab value="single">{t("single")}</Tabs.Tab>
            <Tabs.Tab value="batch">{t("batch")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="single">
            <form
              className="form"
              onSubmit={singleForm.onSubmit(
                values => handleSingleFormSubmit(values),
                errors => {
                  const firstErrorPath = Object.keys(errors)[0];
                  singleForm.getInputNode(firstErrorPath)?.focus();
                }
              )}>
              <div className="form-field">
                <TextInput
                  label={t("consultation-name")}
                  withAsterisk
                  placeholder={t("name")}
                  {...singleForm.getInputProps("name")}
                  key={singleForm.key("name")}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("category")}
                  placeholder={t("select-category")}
                  data={getConsultationServiceCategoryValues()}
                  {...singleForm.getInputProps("category")}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("partner")}
                  placeholder={t("select-partner")}
                  data={partnersOptions}
                  searchable
                  clearable
                  {...singleForm.getInputProps("partner_id")}
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("price")}
                  withAsterisk
                  placeholder="30000"
                  {...singleForm.getInputProps("price")}
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("consultation-service-sessions")}
                  description={t("consultation-service-sessions-description")}
                  withAsterisk
                  min={1}
                  step={1}
                  allowDecimal={false}
                  {...singleForm.getInputProps("sessions")}
                  key={singleForm.key("sessions")}
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("session-interval-days")}
                  description={t("session-interval-days-description")}
                  withAsterisk
                  min={1}
                  step={1}
                  allowDecimal={false}
                  {...singleForm.getInputProps("session_interval_days")}
                  key={singleForm.key("session_interval_days")}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("status")}
                  withAsterisk
                  placeholder={t("select-status")}
                  data={[
                    { label: t("active"), value: "active" },
                    { label: t("inactive"), value: "inactive" },
                  ]}
                  {...singleForm.getInputProps("status")}
                />
              </div>

              <div className="form-actions">
                <PermissionGate permission="resources.create"><Button
                  type="submit"
                  loading={isCreateConsultationServiceLoading}
                  disabled={isCreateConsultationServiceLoading}
                >
                  {t("submit")}
                </Button></PermissionGate>
              </div>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="batch">
            <div className="batch-content">
              <Text fz={16} fw={400} mb="md">
                {t("upload-csv-services")}
              </Text>

              <div className="files-wrapper">
                <div
                  {...getRootProps({
                    className: `dropzone ${isDragActive ? "active" : ""} ${isDragAccept ? "accept" : ""
                      } ${isDragReject ? "reject" : ""}`,
                  })}>
                  <input {...getInputProps()} />
                  <div className="text-content">
                    <div className="svg-wrapper">
                      <IconFileUpload size={28} />
                    </div>
                    <div className="p-wrapper">
                      <p>{t("drag-file-here")}</p>
                      <p>{t("or-click-to-browse")}</p>
                    </div>
                  </div>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="uploaded-files">
                    <h4>{t("uploaded-files")}</h4>
                    <ul>
                      {uploadedFiles.map((file, index) => (
                        <li key={index}>
                          <div className="content">
                            <span>{file.name}</span>
                          </div>
                          <Button
                            variant="subtle"
                            size="xs"
                            color="red"
                            onClick={() => removeFile(file)}>
                            {t("remove")}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <Button
                  mt={"lg"}
                  onClick={handleBatchSubmit}
                  disabled={uploadedFiles.length === 0}
                >
                  {t("submit")}
                </Button>
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>
      </StyledAddConsultationServiceModalContent>
    </Modal>
  );
};
